import { Home, Search, ShoppingCart, User, Heart, Bell, LucideIcon } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCallback } from 'react';
import { useCart } from '@/context/CartContext';
import { useFavorites } from '@/context/FavoritesContext';
import { useAuth } from '@/context/AuthContext';
import { useUnreadNotificationsCount } from '@/hooks/useNotifications';
import { Badge } from '@/components/ui/badge';
import { useIsMobile } from '@/hooks/use-mobile';

interface NavItem {
  icon: LucideIcon;
  label: string;
  href: string;
  showBadge?: boolean;
  showFavoritesBadge?: boolean;
  showNotificationsBadge?: boolean;
  isCart?: boolean;
}

const baseNavItems: NavItem[] = [
  { icon: Home, label: 'Главная', href: '/' },
  { icon: Search, label: 'Каталог', href: '/catalog' },
  { icon: Heart, label: 'Избранное', href: '/favorites', showFavoritesBadge: true },
  { icon: ShoppingCart, label: 'Корзина', href: '/cart', showBadge: true },
];

// Haptic feedback utility
const triggerHaptic = () => {
  if ('vibrate' in navigator) {
    navigator.vibrate(10); // Light tap vibration
  }
};

const MobileBottomNav = () => {
  const location = useLocation();
  const { totalItems } = useCart();
  const { favorites } = useFavorites();
  const { user } = useAuth();
  const unreadCount = useUnreadNotificationsCount();
  const isMobile = useIsMobile();

  // Build navigation items based on auth state
  const navItems: NavItem[] = user 
    ? [
        ...baseNavItems,
        { icon: Bell, label: 'Уведомления', href: '/notifications', showNotificationsBadge: true },
        { icon: User, label: 'Профиль', href: '/profile' },
      ]
    : [
        ...baseNavItems,
        { icon: User, label: 'Профиль', href: '/profile' },
      ];

  const handleItemClick = useCallback(() => {
    triggerHaptic();
  }, []);

  // Known route prefixes — everything else at root level is an invite page (/:code)
  const knownPrefixes = ['/catalog', '/cart', '/product', '/favorites', '/shared', '/s/', '/about', '/contacts', '/auth', '/reset-password', '/profile', '/notifications', '/friends', '/admin', '/partner'];
  const isInvitePage = location.pathname !== '/' && !knownPrefixes.some(p => location.pathname.startsWith(p));

  // Hide on non-mobile, admin pages, friends section, partner section, or invite pages
  if (!isMobile || location.pathname.startsWith('/admin') || location.pathname.startsWith('/friends') || location.pathname.startsWith('/partner') || isInvitePage) {
    return null;
  }

  return (
    <nav className="fixed bottom-5 left-0 right-0 z-50 px-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      <div className="glass-nav mx-auto rounded-2xl shadow-lg border border-white/30 dark:border-white/10">
        <div className="flex items-center justify-around h-14">
          {navItems.map((item) => {
            const isActive = location.pathname === item.href || 
              (item.href === '/catalog' && location.pathname.startsWith('/catalog')) ||
              (item.href === '/cart' && location.pathname === '/cart');
              
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={handleItemClick}
                className="relative flex flex-col items-center justify-center flex-1 h-full py-1.5"
              >
                {isActive && (
                  <motion.div
                    layoutId="glassNavActive"
                    className="absolute inset-1 rounded-xl bg-white/60 dark:bg-white/15"
                    transition={{ type: 'spring', stiffness: 400, damping: 28 }}
                  />
                )}
                <motion.div
                  className="relative z-10"
                  whileTap={{ scale: 0.85 }}
                >
                  <item.icon 
                    className={`w-5 h-5 transition-colors ${
                      isActive ? 'text-primary' : 'text-muted-foreground'
                    }`} 
                  />
                  {item.showBadge && totalItems > 0 && (
                    <Badge 
                      className="absolute -top-2 -right-2 h-4 min-w-4 flex items-center justify-center p-0 text-[10px] bg-primary text-primary-foreground border-0"
                    >
                      {totalItems > 99 ? '99+' : totalItems}
                    </Badge>
                  )}
                  {item.showFavoritesBadge && favorites.length > 0 && (
                    <Badge 
                      className="absolute -top-2 -right-2 h-4 min-w-4 flex items-center justify-center p-0 text-[10px] bg-primary text-primary-foreground border-0"
                    >
                      {favorites.length > 99 ? '99+' : favorites.length}
                    </Badge>
                  )}
                  {item.showNotificationsBadge && unreadCount > 0 && (
                    <Badge 
                      className="absolute -top-2 -right-2 h-4 min-w-4 flex items-center justify-center p-0 text-[10px] bg-primary text-primary-foreground border-0"
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </Badge>
                  )}
                </motion.div>
                <span 
                  className={`relative z-10 text-[10px] mt-0.5 transition-colors ${
                    isActive ? 'text-primary font-medium' : 'text-muted-foreground'
                  }`}
                >
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default MobileBottomNav;
